/**
 * Plan Block Parser
 * Parses [PLAN_BLOCK]...[/PLAN_BLOCK] JSON from AI responses.
 * Used by AiChatMessage and TaskingBotChat to render interactive plan cards.
 */

export interface PlanStep {
  id: number;
  title: string;
  detail?: string;
  status?: 'pending' | 'active' | 'done';
}

export interface PlanOption {
  label: string;
  description?: string;
}

export interface PlanQuestion {
  id: string;
  text: string;
  options: PlanOption[];
}

export interface PlanData {
  title: string;
  description?: string;
  research_summary?: string;
  steps?: PlanStep[];
  question?: PlanQuestion;
  answers?: Record<string, string>;
  status?: 'planning' | 'confirmed' | 'executing';
}

export interface PlanBlock {
  before: string;
  data: PlanData;
  after: string;
}

/** Parse a [PLAN_BLOCK]...[/PLAN_BLOCK] block from content. Returns null if none found. */
export function parsePlanBlock(content: string): PlanBlock | null {
  // Support both [PLAN_BLOCK]{...}[/PLAN_BLOCK] and [PLAN_BLOCK]\n{...}\n[/PLAN_BLOCK]
  // The LLM sometimes omits the closing ] on the opening tag
  let start = content.indexOf('[PLAN_BLOCK]');
  let tagLen = '[PLAN_BLOCK]'.length;
  if (start === -1) {
    // Try without closing bracket: [PLAN_BLOCK\n or [PLAN_BLOCK {
    const altMatch = content.match(/\[PLAN_BLOCK[\s\n]/);
    if (altMatch && altMatch.index !== undefined) {
      start = altMatch.index;
      tagLen = altMatch[0].length;
    }
  }
  if (start === -1) return null;

  const endTag = '[/PLAN_BLOCK]';
  const end = content.indexOf(endTag, start);
  if (end === -1) {
    // Streaming — block not yet closed, skip rendering
    return null;
  }
  const jsonStr = content.slice(start + tagLen, end).trim();
  try {
    const data = JSON.parse(jsonStr) as PlanData;
    return {
      before: content.slice(0, start).trim(),
      data,
      after: content.slice(end + endTag.length).trim(),
    };
  } catch {
    return null;
  }
}

/** Check if content contains a plan block (for quick checks) */
export function hasPlanBlock(content: string): boolean {
  return content.includes('[PLAN_BLOCK]') || /\[PLAN_BLOCK[\s\n]/.test(content);
}

// ── Execution Plan (Plan-First Mode) ─────────────────────────────

export interface ExecPlanStep {
  id: number;
  title: string;
  detail: string;
  tool_hint?: string;
  status: 'pending' | 'active' | 'done' | 'failed' | 'skipped';
  result?: string;
}

export interface ExecPlanData {
  title: string;
  reasoning: string;
  steps: ExecPlanStep[];
  status: 'awaiting_approval' | 'executing' | 'completed' | 'cancelled';
  current_step?: number;
}

export interface ExecPlanBlock {
  before: string;
  data: ExecPlanData;
  after: string;
}

/** Parse an [EXEC_PLAN]...[/EXEC_PLAN] block from content. Returns null if none found. */
export function parseExecPlan(content: string): ExecPlanBlock | null {
  const startTag = '[EXEC_PLAN]';
  const endTag = '[/EXEC_PLAN]';
  const start = content.indexOf(startTag);
  if (start === -1) return null;
  const end = content.indexOf(endTag, start);
  if (end === -1) return null; // Streaming — not closed yet
  const jsonStr = content.slice(start + startTag.length, end).trim();
  try {
    const data = JSON.parse(jsonStr) as ExecPlanData;
    return {
      before: content.slice(0, start).trim(),
      data,
      after: content.slice(end + endTag.length).trim(),
    };
  } catch {
    return null;
  }
}

/** Check if content contains an exec plan block */
export function hasExecPlan(content: string): boolean {
  return content.includes('[EXEC_PLAN]');
}
